import { HttpErrorResponse } from "@angular/common/http";
import { DestroyRef, inject, Injectable } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { ConfirmModalComponent } from "@shared/components/commons/confirm/confirm-modal.component";
import { MarkAsSignedComponent } from "@shared/components/documents-related/mark-as-signed/mark-as-signed.component";
import { DocumentsApiService } from "@shared/services/documents-api.service";
import { ToastrService } from "ngx-toastr";
import { catchError, EMPTY, map, of, switchMap, tap } from "rxjs";
import { DocumentsService } from "./documents.service";
import { ESignApiService } from "./e-sign-api.service";

@Injectable({ providedIn: 'root', })
export class MarkSentSignedCommonService {
  documentsService = inject(DocumentsService);
  documentsApiService = inject(DocumentsApiService);
  eSignService = inject(ESignApiService);
  modalService = inject(NgbModal);
  toastr = inject(ToastrService);
  destroyRef = inject(DestroyRef);

  markAsSigneDoc(documentId: string, inSignedProcess: boolean, earliestChangeDate: Date | null) {
   return this.markAsSignedPopup(earliestChangeDate, inSignedProcess).pipe(
    switchMap(result => {
      if (result.lodge) {
        return this.markAsSignedWithLodgeRequest(
          [documentId],
          result.signingDate
        );
      } else {
        return this.markAsSignedRequest(
          [documentId],
          result.signingDate,
          'Document'
        );
      }
    }),
    takeUntilDestroyed(this.destroyRef))
  }

  markAsSignedStatement(documentId: string, inSignedProcess: boolean, earliestChangeDate: Date | null) {
   return this.markAsSignedPopup(earliestChangeDate, inSignedProcess, false).pipe(
      switchMap((result) => {
          return this.markAsSignedRequest([documentId], result.signingDate, 'Annual Statement');
      }),
      takeUntilDestroyed(this.destroyRef)
    )
  }

  markAsSent(documentsId: string[], isDocument = false){
    return this.markAsSentPopup(isDocument).pipe(
      switchMap((result) => {
        if (result) {
          return this.markAsSentRequest(documentsId, isDocument ? 'Document' : 'Annual Statement')
          .pipe(
            map(() => true),
            catchError(() => {
              return of(false);
            }));
        } else {
          return of(false);
        }
      }),
      takeUntilDestroyed(this.destroyRef)
    );
  }

  markAsSignedDocumentBulk(documentsId: string[], inSignedProcess: boolean, earliestChangeDate: Date | null) {
   return this.markAsSignedPopup(earliestChangeDate, inSignedProcess, true).pipe(
      switchMap((result) => {
        if (result.lodge) {
          return this.markAsSignedWithLodgeRequest(documentsId, result.signingDate);
        } else {
          return this.markAsSignedRequest(documentsId, result.signingDate, 'Documents');
        }
      }),
      takeUntilDestroyed(this.destroyRef)
    )
  }

   markAsSignedStatementBulk(documentsId: string[], inSignedProcess: boolean, earliestChangeDate: Date | null) {
   return this.markAsSignedPopup(earliestChangeDate, inSignedProcess, false).pipe(
      switchMap((result) => {
        if (result.lodge) {
          return this.markAsSignedWithLodgeRequest(documentsId, result.signingDate);
        } else {
          return this.markAsSignedRequest(documentsId, result.signingDate, 'Annual Statements');
        }
      }),
      takeUntilDestroyed(this.destroyRef)
    )
  }

  private markAsSignedPopup(earliestChangeDate: Date | null, inSignedProcess: boolean, isDocument = true) {
    const modalRef = this.modalService.open(MarkAsSignedComponent, { size: 'lg', centered: true  });
    (modalRef.componentInstance as MarkAsSignedComponent).earliestChangeDate = earliestChangeDate ? new Date(earliestChangeDate) : new Date();
    (modalRef.componentInstance as MarkAsSignedComponent).isDocument = isDocument;
    (modalRef.componentInstance as MarkAsSignedComponent).inSignedProcess = inSignedProcess;
    return (modalRef.componentInstance as MarkAsSignedComponent).confirm;
  }

  private markAsSignedRequest(documentId: string[], signedDate: string, typeName: string) {
    return this.eSignService.markAsSign(documentId, signedDate).pipe(
      tap(() => {
        this.toastr.success(`${typeName} was marked as signed`, "Success");
      }),
      catchError((error: HttpErrorResponse) => {
        if(((error.error as { message: string }).message).includes('incorrect status')) {
          this.toastr.error(`${typeName} have incorrect status to mark as signed`, "Error");
        } else {
          this.toastr.error("Error while mark as signed", "Error");
        }
        return EMPTY;
      }),
      takeUntilDestroyed(this.destroyRef)
    )
  }

  private markAsSignedWithLodgeRequest(documentsId: string[], signedDate: string) {
    return this.eSignService.markAsSign(documentsId, signedDate).pipe(
      tap(() => this.toastr.success("Document were marked as signed", "Success")),
      switchMap(() =>
        this.documentsApiService.lodgeDocument(documentsId, false).pipe(
          tap(() => this.toastr.success("Document were sent to lodge", "Success"))
        )
      ),
      catchError((error: HttpErrorResponse) => {
        if(((error.error as { message: string }).message).includes('incorrect status')) {
          this.toastr.error("Document have incorrect status to mark as signed", "Error");
        } else {
          this.toastr.error("Error while mark as signed", "Error");
        }
        return EMPTY;
      }),
      takeUntilDestroyed(this.destroyRef)
    )
  }

  private markAsSentRequest(documentsId: string[], entityName = 'Document') {
    return this.eSignService.markAsSent(documentsId).pipe(
      catchError((error: HttpErrorResponse) => {
        if(((error.error as { message: string }).message).includes('incorrect status')) {
          this.toastr.error(`${entityName} have incorrect status to mark as sent`, "Error");
        } else {
          this.toastr.error("Error while mark as sent", "Error");
        }
        return of('ERR');
      }),
      takeUntilDestroyed(this.destroyRef)
    )
  }

  private markAsSentPopup(isDocument = false) {

    const entityName = isDocument ? 'Document' : 'Annual Statement';

    const modalRef = this.modalService.open(ConfirmModalComponent);
    (modalRef.componentInstance as ConfirmModalComponent).title = 'Mark as Sent';
    (modalRef.componentInstance as ConfirmModalComponent).message = `Marking the ${entityName} as 'Sent' will only change its signing status and will not send the ${entityName}. Do you wish to proceed?`;
    (modalRef.componentInstance as ConfirmModalComponent).confirmText = 'Yes';
    (modalRef.componentInstance as ConfirmModalComponent).confirm = () => {
      return of(true)
    }

     return modalRef.closed.pipe(
      switchMap((confirmed: boolean) => {
        if (confirmed) {
          return of(true);
        }
        return of(false);
      })
    );
  }
}
